import React, { useEffect, useRef, useState } from 'react';
import { GitBranch, Plus, X, Paperclip, Columns, Upload, Download, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiDel, api } from '../../lib/api';
import { errText } from '../../pages/mobile/MobileTendering';

const TYPE_META = {
  our_cost: { label: 'Our Cost', dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  client_boq: { label: 'Client BOQ', dot: 'bg-violet-500', chip: 'bg-violet-50 text-violet-800 border-violet-200' },
};
const GST_RATE = 0.18;
const lbl = 'block text-[11px] font-display font-bold uppercase tracking-wider text-slate-500';
const inp = 'mt-1 w-full border border-slate-300 rounded-md px-2.5 py-1.5 text-sm focus:ring-1 focus:ring-[#3b4872] outline-none';
const fmt = (n) => (n || n === 0 ? `₹${Number(n).toLocaleString('en-IN')}` : '—');
const fmtSize = (b) => (!b ? '' : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const blank = { revision_type: 'our_cost', summary: '', basic: '', notes: '', attached_ids: [] };

export default function EnquiryRevisionsTab({ enqId, docs = [], enqStage, onUpdated }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [compare, setCompare] = useState(false);
  const [uploads, setUploads] = useState([]);   // freshly uploaded revision files
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Already-uploaded enquiry docs that look like BOQs (selectable to re-attach).
  const boqDocs = (docs || []).filter((d) => /boq|priced|bill/i.test(`${d.category || ''} ${d.original_filename || d.label || ''}`));

  const load = async () => {
    setLoading(true);
    try { const r = await apiGet(`/enquiries/${enqId}/revisions`); setItems(r.items || []); }
    catch { toast.error('Could not load revisions'); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (enqId) load(); }, [enqId]);

  // Auto-parse a revision number from an uploaded filename.
  // Looks for patterns: R0, R1, Rev1, Rev.2, Revision 3, v1, v2, etc.
  const _parseRevision = (filename) => {
    const patterns = [
      /\bR(\d+)\b/,               // R0, R1, R2
      /\bRev\.?\s*(\d+)\b/i,      // Rev1, Rev.1, rev 2
      /\bRevision\s*(\d+)\b/i,    // Revision 1, Revision 2
      /[_\-\s]v(\d+)[._\s\b]/i,  // _v1, -v2, v3.xlsx
    ];
    for (const p of patterns) {
      const m = filename.match(p);
      if (m) return `R${m[1]}`;
    }
    return null;
  };

  const handleUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const r = await api.post(`/enquiries/${enqId}/revisions/upload-file`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const uploaded = r.data?.files || [];
      setUploads((u) => [...u, ...uploaded]);
      toast.success(`${uploaded.length} file(s) attached`);
      // Auto-fill summary from filename if still blank (or matches previous auto-fill)
      if (files.length > 0) {
        const parsed = _parseRevision(files[0].name);
        const next = `R${items.length}`;
        setForm((s) => {
          const isAutoFilled = !s.summary.trim() || s.summary === next || /^R\d+$/.test(s.summary);
          if (isAutoFilled && (parsed || !s.summary.trim())) {
            return { ...s, summary: parsed || next };
          }
          return s;
        });
      }
    } catch (e) { toast.error(errText(e, 'Upload failed')); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const removeUpload = (id) => setUploads((u) => u.filter((f) => f.id !== id));

  const downloadFile = async (revId, file) => {
    try {
      const r = await api.get(`/enquiries/${enqId}/revisions/${revId}/files/${file.id}/serve`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = file.original_filename || file.label || 'file'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const submit = async () => {
    if (!form.summary.trim()) { toast.error('Add a summary'); return; }
    setSaving(true);
    try {
      const selected = boqDocs.filter((d) => form.attached_ids.includes(d.id)).map((d) => ({ id: d.id, label: d.original_filename || d.label, category: d.category }));
      const attached = [...selected, ...uploads];
      // Basic is the only entered figure; GST @18% and Total are derived.
      // Stored as {total: basic, tax: gst, grand_total: total} to keep the
      // existing schema (relabelled in the UI as Basic / GST / Total).
      const hasBasic = form.basic !== '' && !Number.isNaN(Number(form.basic));
      const basic = round2(form.basic);
      const gst = round2(basic * GST_RATE);
      const qsum = hasBasic ? { total: basic, tax: gst, grand_total: round2(basic + gst) } : null;
      await apiPost(`/enquiries/${enqId}/revisions`, {
        revision_type: form.revision_type,
        summary: form.summary,
        estimated_value: null,
        notes: form.notes,
        attached_docs: attached,
        quoted_amount_summary: qsum,
      });
      toast.success('Revision added');
      setShowForm(false); setForm(blank); setUploads([]); load();
    } catch { toast.error('Could not add revision'); }
    finally { setSaving(false); }
  };

  const removeRevision = async (rev, displayNo) => {
    if (!window.confirm(`Delete Revision R${displayNo}?\n\nThis cannot be undone.`)) return;
    try {
      await apiDel(`/enquiries/${enqId}/revisions/${rev.id}`);
      toast.success(`Revision R${displayNo} deleted`);
      load();
    } catch { toast.error('Could not delete revision'); }
  };

  const setOutcome = async (rev, outcome, n) => {
    const label = outcome === 'awarded' ? 'AWARDED' : 'LOST';
    const stage = outcome === 'awarded' ? 'Won' : 'Lost';
    let lostReason = null;
    if (outcome === 'lost') {
      // Tendering (30-Sep-2026, batch 2) §2: backend requires lost_reason — ask for it, mandatory.
      lostReason = window.prompt('Lost reason (required — price / technical / scope / client dropped / other):');
      if (!lostReason?.trim()) return;
    } else if (!window.confirm(`Mark R${n} as ${label}?\n\nThis moves the enquiry to ${stage} and carries this revision's value to the award.`)) {
      return;
    }
    try {
      await apiPost(`/enquiries/${enqId}/revisions/${rev.id}/outcome`,
        outcome === 'lost' ? { outcome, lost_reason: lostReason.trim() } : { outcome });
      toast.success(`R${n} marked ${label} — enquiry moved to ${stage}`);
      load();
      // Procurement_7Sep2026 leftover §c: this flips the enquiry's own stage
      // (Won/Lost) — the parent EnquiryDetail header must refresh too, not
      // just this tab's own revisions list.
      onUpdated?.();
    } catch (e) { toast.error(errText(e, 'Could not update outcome')); }
  };

  const OUTCOME_META = {
    awarded: 'bg-emerald-600 text-white border-emerald-600',
    lost: 'bg-rose-600 text-white border-rose-600',
    superseded: 'bg-slate-100 text-slate-500 border-slate-200',
  };

  // Positional, R0-based display numbering (oldest = R0). This stays contiguous
  // even after deleting an in-between revision, regardless of stored revision_no.
  const ordered = [...items].sort((a, b) => (a.revision_no ?? 0) - (b.revision_no ?? 0));
  const displayNoById = {};
  ordered.forEach((r, i) => { displayNoById[r.id] = i; });
  const dno = (r) => displayNoById[r.id] ?? 0;

  return (
    <div className="space-y-4" data-testid="enq-revisions-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GitBranch size={16} className="text-[#3b4872]" />
          <h3 className="font-display font-bold uppercase tracking-wide text-sm">Revisions</h3>
          <span className="text-[11px] text-slate-500">{items.length} rounds</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCompare((v) => !v)} disabled={items.length < 1} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider rounded-sm border ${compare ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'text-slate-600 border-slate-300 hover:bg-slate-100'} disabled:opacity-40`} data-testid="enq-rev-compare-toggle">
            <Columns size={13} /> Compare
          </button>
          <button onClick={() => { setForm({ ...blank, summary: `R${items.length}` }); setUploads([]); setShowForm(true); }} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm hover:bg-[#2d3856] font-display font-bold uppercase tracking-wider" data-testid="enq-rev-add-btn">
            <Plus size={13} /> Add Revision
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-xs text-slate-400">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-400" data-testid="enq-rev-empty">No revisions yet. Track each negotiation round here.</p>
      ) : compare ? (
        <div className="overflow-x-auto border border-slate-200 rounded-md" data-testid="enq-rev-compare">
          <table className="w-full dense-table text-xs">
            <thead><tr><th>Metric</th>{ordered.map((r) => <th key={r.id} className="text-right">R{dno(r)} · {TYPE_META[r.revision_type]?.label}</th>)}</tr></thead>
            <tbody>
              <tr><td className="font-semibold">Type</td>{ordered.map((r) => <td key={r.id} className="text-right">{TYPE_META[r.revision_type]?.label}</td>)}</tr>
              <tr><td className="font-semibold">Basic</td>{ordered.map((r) => <td key={r.id} className="text-right tabular-nums">{fmt(r.quoted_amount_summary?.total)}</td>)}</tr>
              <tr><td className="font-semibold">GST (18%)</td>{ordered.map((r) => <td key={r.id} className="text-right tabular-nums">{fmt(r.quoted_amount_summary?.tax)}</td>)}</tr>
              <tr><td className="font-semibold">Total</td>{ordered.map((r) => <td key={r.id} className="text-right tabular-nums font-bold">{fmt(r.quoted_amount_summary?.grand_total)}</td>)}</tr>
            </tbody>
          </table>
        </div>
      ) : (
        <ol className="relative border-l-2 border-slate-200 ml-2 space-y-4" data-testid="enq-rev-timeline">
          {items.map((r) => {
            const m = TYPE_META[r.revision_type] || {};
            const n = dno(r);
            return (
              <li key={r.id} className="ml-4" data-testid={`enq-rev-${n}`}>
                <span className={`absolute -left-[7px] w-3 h-3 rounded-full ${m.dot}`} />
                <div className="bg-white border border-slate-200 rounded-md p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-bold text-xs">{r.round_label || `R${n}`}</span>
                      <span className={`tag border ${m.chip}`}>{m.label}</span>
                      {r.effective_outcome && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm border uppercase ${OUTCOME_META[r.effective_outcome] || 'bg-slate-100 text-slate-500 border-slate-200'}`} data-testid={`enq-rev-outcome-${n}`}>
                          {r.effective_outcome}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400">{(r.created_at || '').slice(0, 10)}</span>
                      <button onClick={() => removeRevision(r, n)} title="Delete revision" data-testid={`enq-rev-del-${n}`} className="text-slate-400 hover:text-rose-600"><Trash2 size={13} /></button>
                    </div>
                  </div>
                  <p className="text-sm mt-1.5">{r.summary}</p>
                  {r.quoted_amount_summary && (
                    <div className="flex gap-4 mt-2 text-xs">
                      <span>Basic: <b className="tabular-nums">{fmt(r.quoted_amount_summary.total)}</b></span>
                      <span>GST 18%: <b className="tabular-nums">{fmt(r.quoted_amount_summary.tax)}</b></span>
                      <span>Total: <b className="tabular-nums text-[#3b4872]">{fmt(r.quoted_amount_summary.grand_total)}</b></span>
                    </div>
                  )}
                  {(r.attached_docs || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {r.attached_docs.map((d) => (
                        d.source === 'revision_upload' && d.id ? (
                          <button key={d.id} onClick={() => downloadFile(r.id, d)} title="Download" data-testid={`enq-rev-file-dl-${d.id}`}
                            className="inline-flex items-center gap-1 text-[10px] bg-[#eef1f8] border border-[#c9d2ec] text-[#3b4872] rounded px-1.5 py-0.5 hover:bg-[#dfe5f4]">
                            <Download size={9} /> {d.label}{d.size ? <span className="text-slate-400 ml-0.5">· {fmtSize(d.size)}</span> : null}
                          </button>
                        ) : (
                          <span key={d.id} className="inline-flex items-center gap-1 text-[10px] bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5"><Paperclip size={9} /> {d.label}</span>
                        )
                      ))}
                    </div>
                  )}
                  {r.notes ? <p className="text-[11px] text-slate-500 mt-1.5">{r.notes}</p> : null}
                  {/* Procurement_7Sep2026 leftover §a: hide once the enquiry itself has
                      exited (Lost/Parked/On Hold) — Reopen first, then act on the revision.
                      Tendering_30Sep2026 §11: Awarded/Lost is decided on OUR round (our_cost),
                      not on the client's own BOQ (client_boq) — hide it on C rows. */}
                  {!r.outcome && r.revision_type === 'our_cost' && !['Lost', 'Parked', 'On Hold'].includes(enqStage) && (
                    <div className="flex gap-2 mt-2 pt-2 border-t border-slate-100">
                      <button onClick={() => setOutcome(r, 'awarded', n)} data-testid={`enq-rev-award-${n}`}
                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-emerald-300 text-emerald-700 hover:bg-emerald-50">Mark Awarded</button>
                      <button onClick={() => setOutcome(r, 'lost', n)} data-testid={`enq-rev-lost-${n}`}
                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-rose-300 text-rose-700 hover:bg-rose-50">Mark Lost</button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {showForm && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => { setShowForm(false); setUploads([]); }} />
          <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col" data-testid="enq-rev-form">
            <header className="px-5 py-4 border-b flex items-center justify-between bg-[#3b4872] text-white">
              <h2 className="font-display font-bold tracking-wide uppercase text-sm">Add Revision</h2>
              <button onClick={() => { setShowForm(false); setUploads([]); }}><X size={20} /></button>
            </header>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              <div>
                <label className={lbl}>Revision Stream *</label>
                <select value={form.revision_type} onChange={(e) => setForm((s) => ({ ...s, revision_type: e.target.value }))} className={inp} data-testid="enq-rev-type">
                  <option value="our_cost">Our Cost (we → client)</option>
                  <option value="client_boq">Client BOQ (client → us)</option>
                </select>
              </div>
              <div>
                <label className={lbl}>Summary *
                  <span className="ml-1 text-[10px] text-[#7b88bb] font-normal normal-case">(auto-filled from filename, editable)</span>
                </label>
                <input value={form.summary} onChange={(e) => setForm((s) => ({ ...s, summary: e.target.value }))} className={inp} placeholder={`e.g. R${items.length}`} data-testid="enq-rev-summary" />
              </div>
              {(() => {
                const basic = round2(form.basic);
                const gst = round2(basic * GST_RATE);
                const total = round2(basic + gst);
                const has = form.basic !== '' && !Number.isNaN(Number(form.basic));
                return (
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className={lbl}>Basic Amount (₹)</label>
                      <input type="number" value={form.basic} onChange={(e) => setForm((s) => ({ ...s, basic: e.target.value }))} className={inp} placeholder="0" data-testid="enq-rev-basic" />
                    </div>
                    <div>
                      <label className={lbl}>GST @ 18%</label>
                      <input type="text" readOnly tabIndex={-1} value={has ? fmt(gst) : '—'} className={`${inp} bg-slate-50 text-slate-600 cursor-not-allowed`} data-testid="enq-rev-gst" />
                    </div>
                    <div>
                      <label className={lbl}>Total</label>
                      <input type="text" readOnly tabIndex={-1} value={has ? fmt(total) : '—'} className={`${inp} bg-slate-50 font-bold text-[#3b4872] cursor-not-allowed`} data-testid="enq-rev-total" />
                    </div>
                  </div>
                );
              })()}
              {/* Upload updated BOQs / files directly onto this revision */}
              <div>
                <label className={lbl}>Updated BOQ / Files</label>
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); handleUpload(e.dataTransfer.files); }}
                  className="mt-1 border-2 border-dashed border-slate-300 rounded-md p-3 text-center"
                  data-testid="enq-rev-dropzone"
                >
                  <input ref={fileRef} type="file" multiple className="hidden"
                    accept=".xlsx,.xls,.csv,.pdf,.doc,.docx,.png,.jpg,.jpeg,.webp"
                    onChange={(e) => handleUpload(e.target.files)} data-testid="enq-rev-file-input" />
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider bg-white border border-slate-300 rounded-sm hover:border-[#3b4872] disabled:opacity-50"
                    data-testid="enq-rev-upload-btn">
                    {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                    {uploading ? 'Uploading…' : 'Upload files'}
                  </button>
                  <p className="text-[10px] text-slate-400 mt-1.5">Drag &amp; drop or browse · Excel, PDF, Word, images · up to 50 MB each</p>
                </div>
                {uploads.length > 0 && (
                  <div className="mt-2 space-y-1" data-testid="enq-rev-uploaded-list">
                    {uploads.map((f) => (
                      <div key={f.id} className="flex items-center justify-between text-xs bg-[#eef1f8] border border-[#c9d2ec] rounded px-2 py-1" data-testid={`enq-rev-uploaded-${f.id}`}>
                        <span className="inline-flex items-center gap-1.5 truncate text-[#3b4872]"><Paperclip size={11} /> {f.label}<span className="text-slate-400">· {fmtSize(f.size)}</span></span>
                        <button onClick={() => removeUpload(f.id)} className="text-slate-400 hover:text-rose-600 ml-2 shrink-0" data-testid={`enq-rev-remove-${f.id}`}><X size={13} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {boqDocs.length > 0 && (
                <div>
                  <label className={lbl}>…or attach already-uploaded BOQ docs</label>
                  <div className="mt-1 space-y-1 max-h-40 overflow-y-auto border border-slate-200 rounded-md p-2">
                    {boqDocs.map((d) => (
                      <label key={d.id} className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" checked={form.attached_ids.includes(d.id)} onChange={(e) => setForm((s) => ({ ...s, attached_ids: e.target.checked ? [...s.attached_ids, d.id] : s.attached_ids.filter((x) => x !== d.id) }))} data-testid={`enq-rev-doc-${d.id}`} />
                        <Paperclip size={10} /> {d.original_filename || d.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div><label className={lbl}>Notes</label><textarea value={form.notes} onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} rows={2} className={inp} data-testid="enq-rev-notes" /></div>
            </div>
            <footer className="px-5 py-3 border-t flex justify-end gap-2">
              <button onClick={() => { setShowForm(false); setUploads([]); }} className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
              <button onClick={submit} disabled={saving} className="px-4 py-1.5 bg-[#3b4872] text-white text-xs font-display font-bold uppercase tracking-wide rounded-md disabled:opacity-50" data-testid="enq-rev-submit">{saving ? 'Saving…' : 'Add Revision'}</button>
            </footer>
          </div>
        </>
      )}
    </div>
  );
}
